// src/components/Workout/ExerciseCard.js
import React, { useState, useRef, useContext, useEffect } from "react";
import ReplacementModal from "./ReplacementModal";
import describeTempo from "./TempoDesc";
import LanguageContext from "../../../context/LanguageContext";
import FormatInstructions from "../FormatInstructions";
import "../style.css";

// Import data
const jsonDataEn = require("../../data/data-en.json");
const jsonDataFr = require("../../data/data-fr.json");

const ExerciseCard = ({
  exercise,
  index,
  exercises, // full workout array to compute optional
  isOpen,
  toggleOpen,
  handleReplacementChange,
  handleReplaceSubmit,
  selectedReplacement,
  onRemoveOptional = () => {},
}) => {
  const { language } = useContext(LanguageContext);
  const data = language === "en" ? jsonDataEn.data : jsonDataFr.data;
  const exerciseName = language === "en" ? exercise.name : exercise.name_fr;
  const equipment = language === "en" ? exercise.equipment_names : exercise.equipment_names_fr;
  const primaryMuscle = language === "en" ? exercise.primary_muscle : exercise.primary_muscle_fr;
  const secondaryMuscle = language === "en" ? exercise.secondary_muscle : exercise.secondary_muscle_fr;
  const tertiaryMuscle = language === "en" ? exercise.tertiary_muscle : exercise.tertiary_muscle_fr;
  const instructions = language === "en" ? exercise.instructions : exercise.instructions_fr;

  // Determine if this is the optional warmup cardio
  const isOptional = (exercise.exercise_type || "").toLowerCase() === "cardio" && index === 0 && !["cardio", "hiit"].includes((exercises[1]?.exercise_type || "").toLowerCase());

  const [showTooltip, setShowTooltip] = useState(false);
  const [isGifOpen, setGifOpen] = useState(false);

  // refs for click-outside detection
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);

  // close tooltip when clicking outside
  useEffect(() => {
    if (!showTooltip) return;
    const handleClickOutside = (event) => {
      if (iconRef.current && !iconRef.current.contains(event.target) && tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showTooltip]);

  return (
    <div className={`exercise-container mb-5${isOptional ? " optional-card" : ""}`}>
      {/* Exercise Number Badge */}
      <div className="number-badge">{index + 1}</div>
      <div className="row">
        {/* Exercise Image */}
        <div className="col-md-4 d-flex justify-content-center align-items-center">
          <div className="gif-container">
            <img src={`assets/exercise-gif/${exercise.name}.gif`} className="img-fluid" alt={`${exerciseName} Exercise Animation`} style={{ cursor: "pointer" }} onClick={() => setGifOpen(true)} />
          </div>
        </div>

        {/* Exercise Details */}
        <div className="col-md-8">
          <div className="row align-items-center">
            {/* Exercise Name */}
            <div className="col-10 d-flex align-items-center">
              <h2 className="text-start mb-0">
                {exerciseName}
                {isOptional && (
                  <span className="optional-label">
                    {` (OPTIONAL)`}
                    <button type="button" className="optional-remove-btn" aria-label="Remove optional exercise" onClick={() => onRemoveOptional(index)}>
                      &#x2715;
                    </button>
                  </span>
                )}
              </h2>
            </div>
            {/* Replace Icon & Modal */}
            <div className="col-2 text-end">
              <svg
                className="text-color"
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="currentColor"
                data-bs-toggle="modal"
                data-bs-target={`#modal-${index}`}
                style={{ cursor: "pointer" }}
              >
                <path d="M10 11H7.101l.001-.009a4.956 4.956 0 0 1 .752-1.787 5.054 5.054 0 0 1 2.2-1.811c.302-.128.617-.226.938-.291a5.078 5.078 0 0 1 2.018 0 4.978 4.978 0 0 1 2.525 1.361l1.416-1.412a7.036 7.036 0 0 0-2.224-1.501 6.921 6.921 0 0 0-1.315-.408 7.079 7.079 0 0 0-2.819 0 6.94 6.94 0 0 0-1.316.409 7.04 7.04 0 0 0-3.08 2.534 6.978 6.978 0 0 0-1.054 2.505c-.028.135-.043.273-.063.41H2l4 4 4-4zm4 2h2.899l-.001.008a4.976 4.976 0 0 1-2.103 3.138 4.943 4.943 0 0 1-1.787.752 5.073 5.073 0 0 1-2.017 0 4.956 4.956 0 0 1-1.787-.752 5.072 5.072 0 0 1-.74-.61L7.05 16.95a7.032 7.032 0 0 0 2.225 1.5c.424.18.867.317 1.315.408a7.07 7.07 0 0 0 2.818 0 7.031 7.031 0 0 0 4.395-2.945 6.974 6.974 0 0 0 1.053-2.503c.027-.135.043-.273.063-.41H22l-4-4-4 4z" />
              </svg>
              <ReplacementModal
                exercise={exercise}
                index={index}
                selectedReplacement={selectedReplacement}
                handleReplacementChange={handleReplacementChange}
                handleReplaceSubmit={handleReplaceSubmit}
              />
            </div>
          </div>
          {/* Divider */}
          <div className="row">
            <div className="col">
              <hr className="red-divider" />
            </div>
          </div>
          {/* Equipment & Muscles */}
          <div className="row align-items-center">
            <div className="col">
              {equipment && (
                <p className="text-start text-muted fs-5">
                  <strong>{data.workout.equipment}:</strong> {equipment === "N/A" ? "Bodyweight" : equipment}
                </p>
              )}
            </div>
            <div className="col text-end">
              {primaryMuscle && <span className="muscles-badge badge-danger me-1 fs-6">{primaryMuscle}</span>}
              {secondaryMuscle && <span className="muscles-badge badge-warning me-1 fs-6">{secondaryMuscle}</span>}
              {tertiaryMuscle && <span className="muscles-badge badge-success fs-6">{tertiaryMuscle}</span>}
            </div>
          </div>
          {/* Tempo Tooltip */}
          {exercise.tempo && (
            <div className="row">
              <div className="col text-start">
                <div className="tempo-container fs-5">
                  <strong>Tempo:</strong> {exercise.tempo}
                  <span ref={iconRef} className="info-icon" onClick={() => setShowTooltip((v) => !v)} aria-label="More info about tempo">
                    <strong>?</strong>
                  </span>
                  <div ref={tooltipRef} className={`tooltip-box${showTooltip ? " visible" : ""}`} role="tooltip">
                    <div>
                      {data.workout.tempoBefore}
                      <code>{exercise.tempo}</code>
                      {data.workout.tempoAfter}
                      <br />
                      {describeTempo(data.workout.tempo, exercise.tempo)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Sets, Reps, Rest */}
          {exercise.Sets && (
            <div className="row">
              <div className="col text-start fs-5">
                <strong>{data.workout.sets}:</strong> {exercise.Sets}
              </div>
            </div>
          )}
          {exercise.Reps && (
            <div className="row">
              <div className="col text-start fs-5">
                <strong>{exercise.Reps.endsWith("min") ? `${data.workout.duration}:` : `${data.workout.reps}:`}</strong> {exercise.Reps}
              </div>
            </div>
          )}
          {exercise.Rest && exercise.Rest !== "N/A" && (
            <div className="row">
              <div className="col text-start fs-5">
                <strong>{data.workout.rest}:</strong> {exercise.Rest}
              </div>
            </div>
          )}
          {/* Instructions Toggle */}
          <div className="col text-muted fw-bold text-start mt-2" onClick={() => toggleOpen(index)} style={{ cursor: "pointer" }}>
            <p className="fs-5">
              {data.workout.instructions} <i className={`arrow ${isOpen ? "up" : "down"}`}></i>
            </p>
          </div>
          {isOpen && (
            <div className="row">
              <div className="col">
                <FormatInstructions raw={instructions} />
              </div>
            </div>
          )}
        </div>
      </div>
      {/* GIF Lightbox */}
      {isGifOpen && (
        <div className="gif-lightbox-backdrop" onClick={() => setGifOpen(false)}>
          <div className="gif-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="gif-lightbox-close" onClick={() => setGifOpen(false)}>
              &#x2715;
            </button>
            <img src={`assets/exercise-gif/${exercise.name}.gif`} className="gif-lightbox-img" alt={`Enlarged ${exerciseName}`} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseCard;
